import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import FlutterInstallManual from "../../integrate/_snippets/install-flutter-manual.mdx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2 {...{
      "id": "step-one-add-posthog-to-your-app"
    }}>{`Step one: Add PostHog to your app`}</h2>
    <blockquote>
      <p parentName="blockquote">{`🚧 `}<strong parentName="p">{`Note:`}</strong>{` Mobile surveys for Flutter is currently in `}<strong parentName="p">{`beta`}</strong>{`. We'd love to hear your feedback on betas via the `}<a parentName="p" {...{
          "href": "https://us.posthog.com#panel=support%3Afeedback%3Asurveys%3Alow%3Atrue"
        }}>{`in-app support panel`}</a>{` or one of our other `}<a parentName="p" {...{
          "href": "/docs/support-options"
        }}>{`support options`}</a>{`.`}</p>
    </blockquote>
    <p>{`Using it requires PostHog's Flutter SDK version >= 5.4.1, but it's recommended to always use the latest version.`}</p>

    <blockquote>
      <p parentName="blockquote">{`⚠️ `}<strong parentName="p">{`Note:`}</strong>{` For mobile surveys, you must setup the SDK manually.`}</p>
    </blockquote>
    <FlutterInstallManual mdxType="FlutterInstallManual" />
    <h2 {...{
      "id": "step-two-enable-surveys-in-your-configuration"
    }}>{`Step two: Enable surveys in your configuration`}</h2>
    <p>{`To enable surveys in your Flutter app, update your PostHog configuration:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart"
      }}>{`final config = PostHogConfig('<ph_project_api_key>');
config.host = 'https://us.i.posthog.com';
config.surveys = true; // Enable surveys

await Posthog().setup(config);
`}</code></pre>
    <h2 {...{
      "id": "step-three-install-posthogobserver"
    }}>{`Step three: Install PosthogObserver`}</h2>
    <p>{`For surveys to be shown, you need to add the `}<inlineCode parentName="p">{`PosthogObserver`}</inlineCode>{` to your app. The observer allows PostHog to determine the appropriate context for displaying surveys.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart"
      }}>{`import 'package:flutter/material.dart';

import 'package:posthog_flutter/posthog_flutter.dart';

class MyApp extends StatefulWidget {
  const MyApp({super.key});

  @override
  State<MyApp> createState() => _MyAppState();
}

class _MyAppState extends State<MyApp> {
  @override
  void initState() {
    super.initState();
  }

  @override
  Widget build(BuildContext context) {
    return PostHogWidget(
      child: MaterialApp(
        navigatorObservers: [PosthogObserver()],
        title: 'My App',
        home: const HomeScreen(),
      ),
    );
  }
}
`}</code></pre>
    <p>{`If you're using `}<inlineCode parentName="p">{`go_router`}</inlineCode>{`, check `}<a parentName="p" {...{
        "href": "/docs/libraries/flutter#capturing-screen-views"
      }}>{`this page`}</a>{` to learn how to set up the `}<inlineCode parentName="p">{`PosthogObserver`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "supported-features"
    }}>{`Supported Features`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Feature`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Support`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Questions`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`All question types`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Multi-question surveys`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Confirmation message`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Feedback button presentation`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Conditional questions`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Customization / Appearance`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Set colors in PostHog dashboard`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Shuffle questions`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`PostHog branding`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Delay popup after screen view`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Position config`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌ Always bottom sheet`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`HTML content`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Display conditions`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Feature flag & property targeting`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Screen/URL targeting`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Device type targeting`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Survey wait period`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Event triggers`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Action triggers`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Partial responses`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "limitations"
    }}>{`Limitations`}</h2>
    <ul>
      <li parentName="ul">{`On Android, requires API >= 26.`}</li>
      <li parentName="ul">{`On iOS, minimum deployment target is `}<a parentName="li" {...{
          "href": "/docs/libraries/ios"
        }}>{`iOS13`}</a>{`.`}</li>
      <li parentName="ul">{`Requires PostHog Flutter SDK version >= 5.4.0`}</li>
    </ul>
    <h2 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h2>
    <ul>
      <li parentName="ul">{`Update your SDK and iOS Pods.`}</li>
      <li parentName="ul">{`Make sure to set the minimum platform version to iOS 13.0 in your Podfile.`}</li>
      <li parentName="ul">{`Make sure you have disabled automatic SDK intialization. `}</li>
      <li parentName="ul">{`Run a clean build if you experience issues.`}</li>
      <li parentName="ul">{`If surveys are not being displayed when triggered, make sure you have installed the `}<a parentName="li" {...{
          "href": "/docs/surveys/installation?tab=Flutter#step-three-install-posthogobserver"
        }}>{`PosthogObserver`}</a>{`.`}
        <ul parentName="li">
          <li parentName="ul">{`Using `}<a parentName="li" {...{
              "href": "/docs/libraries/flutter#capturing-screen-views#using-navigatorobservers"
            }}>{`navigationObservers`}</a></li>
          <li parentName="ul">{`Using `}<a parentName="li" {...{
              "href": "/docs/libraries/flutter#capturing-screen-views#using-go-router"
            }}>{`go_router`}</a></li>
        </ul>
      </li>
      <li parentName="ul">{`If you are using multiple navigation observers and encounter a log message `}<inlineCode parentName="li">{`[PostHog] Cannot show survey: No valid context found...`}</inlineCode>{`, try placing `}<inlineCode parentName="li">{`PosthogObserver`}</inlineCode>{` first in the list of observers. We've noticed that order sometimes matters due to potential interference.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      